import clsx from 'clsx';
import React from 'react';
import { makeStyles } from 'tss-react/mui';
import { IFormFieldInputBaseProps } from '@components/form-fields';
import { MAIN_THEME } from '@styles';
import Tooltip from '@mui/material/Tooltip';
import InfoIcon from '@mui/icons-material/Info';

export interface IFormFieldWrapperProps extends IFormFieldInputBaseProps {
  name: string;
  title: string;
  material?: boolean;
  error?: string;
  hasTooltip?: boolean;
  tooltipText?: string;
  hideError?: boolean;
  fullWidth?: boolean;
  children: React.ReactNode;
  classes?: {
    label?: string;
  };
}

const useStyles = makeStyles()((theme) => ({
  labelWrapper: {
    display: 'block',
    textAlign: 'left',
    fontSize: `${MAIN_THEME.fontSize.sm}`,
    lineHeight: '15px',
  },
  label: {
    display: 'block',
    color: MAIN_THEME.color.black,
    textAlign: 'left',
    margin: '0 0 10px',
    fontFamily: MAIN_THEME.font.medium,
    fontSize: MAIN_THEME.fontSize.sm,
  },
  materialLabel: {
    fontSize: MAIN_THEME.fontSize.sm,
    lineHeight: '16px',
    fontFamily: MAIN_THEME.font.medium,
    margin: '0px',
    padding: '0px',
  },
  description: {
    fontSize: MAIN_THEME.fontSize.xs,
    fontStyle: 'italic',
    marginLeft: '5px',
    color: MAIN_THEME.color.black,
  },
  error: {
    textAlign: 'right',
    fontSize: MAIN_THEME.fontSize.xs,
    minHeight: '15px',
    display: 'block',
    color: MAIN_THEME.color.red,
    margin: '5px 0 0 ',
  },
  childrenContainer: {
    position: 'relative',
  },
  headerContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    direction: 'ltr',
  },
  actionsContainer: {
    display: 'flex',
    flexDirection: 'row',
  },
  capsLockWarning: {
    color: MAIN_THEME.color.red,
    fontSize: MAIN_THEME.fontSize.xs,
    display: 'block',
    marginTop: '-15px',
    textAlign: 'left',
  },
  tooltipIcon: {
    marginLeft: '5px',
    cursor: 'pointer',
    width: '12px',
    height: '12px',
  },
  tooltip: {
    backgroundColor: 'white',
    color: 'black',
    boxShadow: theme.shadows[1],
    fontSize: MAIN_THEME.fontSize.xs,
    padding: '10px',
  },
  arrow: {
    color: 'white',
    '&::before': {
      boxShadow: theme.shadows[1],
    },
  },
  fullWidth: {
    gridColumn: '1 / -1',
    width: '100%',
  },
}));

const FormFieldWrapper: React.FC<IFormFieldWrapperProps> = ({
  className,
  error,
  style,
  name,
  title,
  hideError,
  fullWidth,
  children,
  hasTooltip = false,
  tooltipText,
  material = false,
}) => {
  const { classes: labelClasses } = useStyles();

  return (
    <label
      htmlFor={name}
      className={clsx(
        labelClasses.labelWrapper,
        className,
        fullWidth && labelClasses.fullWidth
      )}
      style={style}
    >
      <div className={labelClasses.headerContainer}>
        <span
          className={material ? labelClasses.materialLabel : labelClasses.label}
        >
          {title}
          {hasTooltip && tooltipText !== '' && (
            <Tooltip
              title={tooltipText || ''}
              placement="top"
              classes={{
                tooltip: labelClasses.tooltip,
                arrow: labelClasses.arrow,
              }}
              arrow
            >
              <InfoIcon className={labelClasses.tooltipIcon} />
            </Tooltip>
          )}
        </span>
      </div>
      <div className={labelClasses.childrenContainer}>{children}</div>
      {!hideError && (
        <span id={`errorMsg-${name}`} className={labelClasses.error}>
          {error}
        </span>
      )}
    </label>
  );
};

export default FormFieldWrapper;
